/**
 * WHICH ENGINE A RESULT CAME FROM, said wherever positions are shown.
 *
 * Most of the room's searches are not run on Google: on room 41 all 1,360
 * were StartPage, Yahoo or DuckDuckGo. Those show other engines' results
 * (StartPage shows Google's; Yahoo and DuckDuckGo mostly Bing's), so a
 * position here can differ from what someone sees on google.com, and a reader
 * who assumes Google reads every one of them wrong.
 */

/** Whose results an engine shows, where it is not its own index. */
export const ENGINE_SOURCE: Record<string, string> = {
  StartPage: "Google's results",
  Yahoo: "Bing's results",
  DuckDuckGo: "mostly Bing's results",
  Ecosia: "mostly Bing's results",
  Qwant: "its own and Bing's results",
  Brave: 'its own index',
};

/** "StartPage (Google's results)", or the engine alone when it is its own. */
export function engineLabel(engine: string | null | undefined): string {
  if (!engine) return 'unknown engine';
  const source = ENGINE_SOURCE[engine];
  return source ? `${engine} (${source})` : engine;
}

/** A small tag beside a search or a position. */
export function EngineTag({ engine }: { engine: string | null | undefined }) {
  if (!engine) return null;
  return (
    <span
      className="ml-1 rounded bg-gray-100 px-1 text-[11px] text-gray-500 dark:bg-gray-700 dark:text-gray-300"
      title={`Searched on ${engineLabel(engine)}`}
      data-testid="engine-tag"
    >
      {engine}
    </span>
  );
}

/** Count of searches per engine, from rows that carry one. */
export function engineCounts(rows: { engine?: string | null }[]): Record<string, number> {
  const out: Record<string, number> = {};
  for (const r of rows) {
    if (r.engine) out[r.engine] = (out[r.engine] ?? 0) + 1;
  }
  return out;
}

/** One line: which engines these searches ran on, and whether Google was one. */
export function EnginesNote({ counts, className = '' }: { counts?: Record<string, number> | null; className?: string }) {
  const entries = Object.entries(counts ?? {}).sort((a, b) => b[1] - a[1]);
  if (!entries.length) return null;
  const google = entries.some(([e]) => e === 'Google');
  return (
    <p className={`text-xs text-gray-500 dark:text-gray-400 ${className}`} data-testid="engines-note">
      Searched on{' '}
      {entries.map(([e, n], i) => (
        <span key={e}>
          {i > 0 && ' · '}
          <span title={engineLabel(e)}>
            {engineLabel(e)} {n.toLocaleString()}
          </span>
        </span>
      ))}
      .
      {!google && ' Not Google directly, so positions can differ from what you see on google.com.'}
    </p>
  );
}
